// Exportação dos medidores coletados em campo (módulo Motoboy).
// Excel/CSV/PDF genéricos + planilha específica p/ consulta de UCs na Equatorial.
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildSheet, downloadWorkbook, fileStamp, ColType } from './excelExport';

export interface MedidorExportRow {
    paradaId: string;
    empresa: string;
    cnpj: string;
    endereco: string;
    cidade: string;
    distribuidora: string;
    numeroMedidor: string;
    dataCaptura: string; // dd/mm/aaaa hh:mm
    operador: string;
    latitude: number | null;
    longitude: number | null;
}

export const DISTRIBUIDORA_PADRAO = 'Equatorial GO';

const HEADERS = ['Empresa', 'CNPJ', 'Endereço', 'Cidade', 'Distribuidora', 'Nº Medidor', 'Data da Captura', 'Operador', 'Latitude', 'Longitude'];
const COL_TYPES: ColType[] = ['txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'];

const linha = (r: MedidorExportRow): string[] => [
    r.empresa, r.cnpj, r.endereco, r.cidade, r.distribuidora, r.numeroMedidor,
    r.dataCaptura, r.operador,
    r.latitude != null ? String(r.latitude) : '', r.longitude != null ? String(r.longitude) : '',
];

export const exportMedidoresExcel = (rows: MedidorExportRow[]) => {
    const ws = buildSheet(HEADERS, rows.map(linha), COL_TYPES, {
        widths: [32, 20, 40, 18, 16, 16, 18, 22, 12, 12],
    });
    downloadWorkbook([{ name: 'Medidores', ws }], `medidores_${fileStamp()}.xlsx`);
};

export const exportMedidoresCsv = (rows: MedidorExportRow[]) => {
    const esc = (v: string) => /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const linhas = [HEADERS.join(';'), ...rows.map(r => linha(r).map(esc).join(';'))];
    // BOM p/ o Excel pt-BR abrir com acentuação correta
    const blob = new Blob(['﻿' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `medidores_${fileStamp()}.csv`);
};

export const exportMedidoresPdf = (rows: MedidorExportRow[]) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(13);
    doc.text('Medidores Coletados — VigorHub', 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} · ${rows.length} registro(s)`, 14, 20);
    autoTable(doc, {
        startY: 25,
        head: [HEADERS],
        body: rows.map(linha),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [74, 78, 63] }, // paleta olive VigorHub
        alternateRowStyles: { fillColor: [245, 245, 240] },
    });
    doc.save(`medidores_${fileStamp()}.pdf`);
};

/** Planilha enxuta no formato pedido p/ envio à Equatorial (consulta de UCs). */
export const exportMedidoresEquatorial = (rows: MedidorExportRow[]) => {
    const headers = ['Nome da Empresa', 'CNPJ', 'Endereço Completo', 'Município', 'Número do Medidor', 'Data da Coleta', 'Operador Responsável'];
    const ws = buildSheet(
        headers,
        rows.map(r => [r.empresa, r.cnpj, r.endereco, r.cidade, r.numeroMedidor, r.dataCaptura, r.operador]),
        ['txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'],
        { widths: [34, 20, 44, 20, 18, 18, 24] },
    );
    downloadWorkbook([{ name: 'Medidores Equatorial', ws }], `medidores_equatorial_${fileStamp()}.xlsx`);
};

/** Agrupa números de medidor repetidos (mesmo nº em mais de uma captura). */
export const encontrarDuplicados = (rows: MedidorExportRow[]): Map<string, MedidorExportRow[]> => {
    const porNumero = new Map<string, MedidorExportRow[]>();
    for (const r of rows) {
        if (!r.numeroMedidor) continue;
        const grupo = porNumero.get(r.numeroMedidor) || [];
        grupo.push(r);
        porNumero.set(r.numeroMedidor, grupo);
    }
    return new Map(Array.from(porNumero.entries()).filter(([, g]) => g.length > 1));
};
